import { LinearProgress, MenuItem, Select } from '@mui/material';
import { useCallback, useState } from 'react';
import { formatEnumForDisplay, formatPoiStatus } from '@/utils';
import { useUpdateWorkpackStatusMutation, WorkpackStatus } from '@/__generated__/graphql';
import { useSnackBarMessage } from '@/utils/useSnackBarMessage';
import { primary } from '@/theme/colors';

type Props = {
  workpackId: string;
  status: WorkpackStatus | null | undefined;
  showAlternateStatuses: boolean;
};

const ALTERNATE_STATUSES = new Set([
  WorkpackStatus.DesktopReviewInProgress,
  WorkpackStatus.DesktopReviewComplete,
  WorkpackStatus.FieldVerificationInProgress,
  WorkpackStatus.FieldVerificationComplete,
  WorkpackStatus.FinalReviewInProgress,
  WorkpackStatus.FinalReviewComplete,
]);

export const StatusChangeDropDown = ({
  workpackId,
  status,
  showAlternateStatuses = false,
}: Props) => {
  const [selectedStatus, setSelectedStatus] = useState<WorkpackStatus | ''>(status || '');
  const { showSnackBar } = useSnackBarMessage({ variant: 'filled' });

  const [updateWorkpackStatus, { loading }] = useUpdateWorkpackStatusMutation();

  const handleStatusChange = useCallback(
    (updatedStatus: WorkpackStatus) => {
      updateWorkpackStatus({
        variables: { workpackId, status: updatedStatus },
        onCompleted: () => {
          showSnackBar('Workpack status has been updated successfully', 'success');
          setSelectedStatus(updatedStatus);
        },
        onError: (error) => {
          showSnackBar(error.message, 'error');
        },
      });
    },
    [showSnackBar, updateWorkpackStatus, workpackId]
  );

  const renderValue = (value: WorkpackStatus | '') => {
    return value ? (
      formatPoiStatus(value)
    ) : (
      <span style={{ color: primary.LightGrey }}>Update Status</span>
    );
  };

  return (
    <>
      {loading ? (
        <LinearProgress color="info" sx={{ height: 7 }} />
      ) : (
        <Select
          value={selectedStatus}
          onChange={({ target: { value } }) => handleStatusChange(value as WorkpackStatus)}
          sx={{ height: 32, width: 'fit-content', fontSize: '1.2rem' }}
          fullWidth
          displayEmpty
          renderValue={renderValue}
        >
          {Object.values(WorkpackStatus)
            .filter((workpackStatus) =>
              showAlternateStatuses
                ? ALTERNATE_STATUSES.has(workpackStatus)
                : !ALTERNATE_STATUSES.has(workpackStatus)
            )
            .map((workpackStatus) => (
              <MenuItem key={workpackStatus} value={workpackStatus}>
                {formatEnumForDisplay(workpackStatus)}
              </MenuItem>
            ))}
        </Select>
      )}
    </>
  );
};
